if (!customElements.get('product-info')) {
  customElements.define(
    'product-info',
    class ProductInfo extends HTMLElement {
      quantityInput = undefined;
      quantityForm = undefined;
      onVariantChangeUnsubscriber = undefined;
      cartUpdateUnsubscriber = undefined;
      abortController = undefined;
      pendingRequestUrl = null;
      preProcessHtmlCallbacks = [];
      postProcessHtmlCallbacks = [];
      onLocalVariantChange = undefined;
      onStrapColorClick = undefined;
      strapProductCache = new Map();
      lastAppliedVariantKey = null;

      constructor() {
        super();

        this.quantityInput = this.querySelector('.quantity__input');
        this.onLocalVariantChange = this.handleLocalVariantChange.bind(this);
        this.onStrapColorClick = this.handleStrapColorClick.bind(this);
      }

      connectedCallback() {
        this.initializeProductSwapUtility();

        this.onVariantChangeUnsubscriber = subscribe(
          PUB_SUB_EVENTS.optionValueSelectionChange,
          this.handleOptionValueChange.bind(this)
        );

        this.addEventListener('variant:change', this.onLocalVariantChange);
        this.addEventListener('click', this.onStrapColorClick, true);
        this.cacheStrapColorProducts();
        this.preloadStrapColorImages();

        this.initQuantityHandlers();
        this.dispatchEvent(new CustomEvent('product-info:loaded', { bubbles: true }));
      }

      addPreProcessCallback(callback) {
        this.preProcessHtmlCallbacks.push(callback);
      }

      initQuantityHandlers() {
        if (!this.quantityInput) return;

        this.quantityForm = this.querySelector('.product-form__quantity');
        if (!this.quantityForm) return;

        this.setQuantityBoundries();
        if (!this.dataset.originalSection) {
          this.cartUpdateUnsubscriber = subscribe(PUB_SUB_EVENTS.cartUpdate, (event = {}) => {
            if (event.source === 'custom-product-form') return;
            this.fetchQuantityRules();
          });
        }
      }

      disconnectedCallback() {
        this.removeEventListener('variant:change', this.onLocalVariantChange);
        this.removeEventListener('click', this.onStrapColorClick, true);
        this.onVariantChangeUnsubscriber?.();
        this.cartUpdateUnsubscriber?.();
      }

      initializeProductSwapUtility() {
        this.preProcessHtmlCallbacks.push((html) =>
          html.querySelectorAll('.scroll-trigger').forEach((element) => element.classList.add('scroll-trigger--cancel'))
        );
        this.postProcessHtmlCallbacks.push((newNode) => {
          window?.Shopify?.PaymentButton?.init();
          window?.ProductModel?.loadShopifyXR();
        });
      }

      handleOptionValueChange({ data: { event } }) {
        if (!this.contains(event.target)) return;

        this.resetProductFormState();

        const variantSelects = event.target.closest('variant-selects') || this.variantSelectors;
        const variant = variantSelects?.currentVariant || this.findVariantFromCurrentOptions(variantSelects);

        if (!variant) {
          this.setUnavailable();
          return;
        }

        this.applyVariantUpdate(variant);
      }

      handleLocalVariantChange(event) {
        if (!this.contains(event.target)) return;
        if (!event.detail?.variant) return;

        this.applyVariantUpdate(event.detail.variant);
      }

      async handleStrapColorClick(event) {
        const button = event.target.closest('.strap-link');
        if (!button || !this.contains(button)) return;

        event.preventDefault();
        event.stopPropagation();

        const productUrl = button.dataset.productUrl || button.getAttribute('href') || '';
        if (productUrl && !button.dataset.productUrl) button.dataset.productUrl = productUrl;

        let productData = this.getStrapProductData(button);
        if (!productData?.variants?.length && productUrl) {
          productData = await this.fetchStrapProductData(productUrl, button.dataset.productHandle);
        }
        if (!productData?.variants?.length) return;

        this.applyStrapProductData(productData, button);
      }

      cacheStrapColorProducts() {
        this.querySelectorAll('.strap-color-product-json').forEach((script) => {
          try {
            const productData = JSON.parse(script.textContent);
            const keys = [script.dataset.productUrl, script.dataset.productHandle, productData.url, productData.handle];
            keys.filter(Boolean).forEach((key) => this.strapProductCache.set(key, productData));
          } catch (error) {
            // Ignore malformed metafield data and let the current product continue to work.
          }
        });
      }

      preloadStrapColorImages() {
        this.strapProductCache.forEach((productData) => {
          this.getProductImageUrls(productData)
            .slice(0, 3)
            .forEach((src) => {
              const image = new Image();
              image.src = this.imageWithWidth(src, 823);
            });
        });
      }

      getStrapProductData(button) {
        const cacheKey = button.dataset.productUrl || button.dataset.productHandle;
        if (cacheKey && this.strapProductCache.has(cacheKey)) return this.strapProductCache.get(cacheKey);

        const selector = [
          button.dataset.productUrl ? `.strap-color-product-json[data-product-url="${CSS.escape(button.dataset.productUrl)}"]` : '',
          button.dataset.productHandle ? `.strap-color-product-json[data-product-handle="${CSS.escape(button.dataset.productHandle)}"]` : '',
        ]
          .filter(Boolean)
          .join(',');
        const script = selector ? this.querySelector(selector) : null;

        if (!script) return null;

        try {
          const productData = JSON.parse(script.textContent);
          [button.dataset.productUrl, button.dataset.productHandle, productData.url, productData.handle]
            .filter(Boolean)
            .forEach((key) => this.strapProductCache.set(key, productData));
          return productData;
        } catch (error) {
          return null;
        }
      }

      fetchStrapProductData(productUrl, productHandle) {
        const normalizedUrl = productUrl.startsWith('http') ? new URL(productUrl) : new URL(productUrl, window.location.origin);
        const handle = productHandle || normalizedUrl.pathname.split('/').filter(Boolean).pop();
        if (!handle) return Promise.resolve(null);

        const cacheKey = normalizedUrl.pathname;
        if (this.strapProductCache.has(cacheKey)) return Promise.resolve(this.strapProductCache.get(cacheKey));

        return fetch(`${window.location.origin}/products/${handle}.js`, {
          headers: { Accept: 'application/json' },
        })
          .then((response) => (response.ok ? response.json() : null))
          .then((productData) => {
            if (!productData) return null;
            const normalizedData = this.normalizeProductData(productData, normalizedUrl.pathname);
            [cacheKey, normalizedUrl.pathname, handle, normalizedData.url, normalizedData.handle]
              .filter(Boolean)
              .forEach((key) => this.strapProductCache.set(key, normalizedData));
            return normalizedData;
          })
          .catch(() => null);
      }

      normalizeProductData(productData, productUrl = '') {
        if (!productData) return productData;

        const optionNames = (productData.options || []).map((option) =>
          typeof option === 'string' ? option : option?.name
        ).filter(Boolean);

        return {
          ...productData,
          url: productData.url || productUrl || (productData.handle ? `/products/${productData.handle}` : ''),
          options: optionNames,
        };
      }

      applyStrapProductData(productData, activeButton) {
        this.updateStrapButtons(activeButton);
        this.updateProductDataScripts(productData);
        this.updateProductTitle(productData);

        const variantSelects = this.variantSelectors;
        if (variantSelects) {
          variantSelects.variantData = productData.variants || [];
          variantSelects.optionNames = this.getProductOptionNames(productData, variantSelects);
        }

        const variant = this.pickVariantForProduct(productData, variantSelects);
        if (!variant) {
          this.setUnavailable();
          return;
        }

        this.selectVariantOptions(variantSelects, variant, this.getProductOptionNames(productData, variantSelects));
        if (variantSelects) {
          variantSelects.currentVariant = variant;
          variantSelects.updateActiveLabels?.();
          variantSelects.updateAvailability?.();
        }

        this.applyVariantUpdate(variant, productData);
        variantSelects?.dispatchEvent(
          new CustomEvent('variant:change', {
            detail: { variant },
            bubbles: true,
          })
        );
      }

      updateStrapButtons(activeButton) {
        this.querySelectorAll('.strap-link').forEach((button) => {
          const isActive = button === activeButton;
          button.classList.toggle('current__active', isActive);
          button.classList.toggle('active', isActive);
          button.setAttribute('aria-current', isActive ? 'true' : 'false');
          button.querySelector('.variant-label-color')?.classList.toggle('current-active-labelx', isActive);
        });

        const colourName = activeButton.querySelector('.variant-label-color')?.dataset.variantName;
        const colourLabel = this.querySelector('.currentx-valx');
        if (colourName && colourLabel) colourLabel.textContent = colourName;
      }

      updateProductDataScripts(productData) {
        const productJson = this.querySelector('#product-json');
        const variantsJson = this.querySelector('#product-variants-json');
        const buyButtonVariantsJson = this.querySelector('script[id^="ProductJson-"]');

        if (productJson) productJson.textContent = JSON.stringify(productData);
        if (variantsJson) variantsJson.textContent = JSON.stringify(productData.variants || []);
        if (buyButtonVariantsJson) {
          buyButtonVariantsJson.id = `ProductJson-${productData.id}`;
          buyButtonVariantsJson.textContent = JSON.stringify(productData.variants || []);
        }

        const productForm = this.querySelector('form[data-type="add-to-cart-form"]');
        if (productForm) {
          productForm.dataset.productId = productData.id;
          productForm.dataset.productTitle = productData.title || '';
        }

        const giftWrapData = this.querySelector('#gift-wrap-data');
        if (giftWrapData) {
          giftWrapData.dataset.productId = productData.id;
          giftWrapData.dataset.productTitle = productData.title || '';
          giftWrapData.dataset.productHandle = productData.handle || '';
        }
      }

      updateProductTitle(productData) {
        if (!productData?.title) return;

        this.querySelectorAll('.product__title h1, .product__title h2').forEach((heading) => {
          heading.textContent = productData.title;
        });

        const successTitle = this.querySelector('#cart-success .basket-title strong');
        if (successTitle) successTitle.textContent = productData.title;
      }

      pickVariantForProduct(productData, variantSelects = this.variantSelectors) {
        const variants = productData?.variants || [];
        const optionNames = this.getProductOptionNames(productData, variantSelects);
        const selectedMap = this.getSelectedOptionMap(variantSelects);

        const exactMatch = variants.find((variant) =>
          variant.options.every((optionValue, index) => {
            const optionName = optionNames[index];
            return !selectedMap[optionName] || selectedMap[optionName] === optionValue;
          })
        );

        return exactMatch || variants.find((variant) => variant.available) || variants[0] || null;
      }

      getSelectedOptionMap(variantSelects = this.variantSelectors) {
        if (!variantSelects) return {};

        return Array.from(variantSelects.querySelectorAll('.product-form__input[data-option-name]')).reduce(
          (selectedOptions, fieldset) => {
            const checked = fieldset.querySelector('input[type="radio"]:checked');
            const select = fieldset.querySelector('select');
            selectedOptions[fieldset.dataset.optionName] = checked?.value || select?.value || '';
            return selectedOptions;
          },
          {}
        );
      }

      selectVariantOptions(variantSelects, variant, optionNames) {
        if (!variantSelects || !variant) return;

        variant.options.forEach((optionValue, index) => {
          const optionName = optionNames[index];
          const fieldset = variantSelects.querySelector(`.product-form__input[data-option-name="${CSS.escape(optionName)}"]`);
          if (!fieldset) return;

          const select = fieldset.querySelector('select');
          if (select) {
            select.value = optionValue;
            Array.from(select.options).forEach((option) => {
              option.toggleAttribute('selected', option.value === optionValue);
            });
          }

          fieldset.querySelectorAll('input[type="radio"]').forEach((radio) => {
            radio.checked = radio.value === optionValue;
          });

          const labelSpan = fieldset.closest('.product-form__input--dropdown')?.querySelector('.form__label span');
          if (labelSpan) labelSpan.textContent = optionValue;
        });
      }

      findVariantFromCurrentOptions(variantSelects = this.variantSelectors) {
        const productData = this.getProductData();
        if (!productData?.variants?.length || !variantSelects) return null;

        const optionNames = this.getProductOptionNames(productData, variantSelects);
        const selectedMap = this.getSelectedOptionMap(variantSelects);

        return productData.variants.find((variant) =>
          variant.options.every((optionValue, index) => selectedMap[optionNames[index]] === optionValue)
        );
      }

      applyVariantUpdate(variant, productData = this.getProductData()) {
        if (!variant) {
          this.setUnavailable();
          return;
        }

        const variantKey = `${productData?.id || this.dataset.productId || ''}:${variant.id}`;
        if (this.lastAppliedVariantKey === variantKey) return;
        this.lastAppliedVariantKey = variantKey;

        this.updateVariantInputs(variant.id);
        this.updateSelectedVariantJson(variant);
        this.updateProductFormDataset(variant, productData);
        this.updatePriceFromVariant(variant);
        this.updateSkuFromVariant(variant);
        this.updateTrustpilotWidgetsFromVariant(variant);
        this.updateInventoryFromVariant(variant);
        this.updateAddToCartState(variant);
        this.updateMediaFromVariant(variant, productData);
        this.pickupAvailability?.update?.(variant);

        publish(PUB_SUB_EVENTS.variantChange, {
          data: {
            sectionId: this.sectionId,
            html: document,
            variant,
          },
        });
      }

      updateSelectedVariantJson(variant) {
        this.querySelectorAll('[data-selected-variant]').forEach((script) => {
          script.textContent = JSON.stringify(variant);
        });
      }

      updateProductFormDataset(variant, productData) {
        const productForm = this.querySelector('form[data-type="add-to-cart-form"]');
        if (!productForm) return;

        productForm.dataset.variantId = variant.id;
        productForm.dataset.variantTitle = variant.title || variant.options?.join(' / ') || '';
        if (productData?.id) productForm.dataset.productId = productData.id;
        if (productData?.title) productForm.dataset.productTitle = productData.title;
      }

      updatePriceFromVariant(variant) {
        const priceWrapper = this.querySelector(`#price-${this.dataset.section}`);
        const price = priceWrapper?.querySelector('.price');
        if (!price) return;

        const moneyPrice = this.formatMoney(variant.price);
        const compareAtPrice = variant.compare_at_price;
        const isOnSale = compareAtPrice && compareAtPrice > variant.price;

        price.classList.toggle('price--sold-out', !variant.available);
        price.classList.toggle('price--on-sale', Boolean(isOnSale));

        price.querySelectorAll('.price__regular .price-item--regular').forEach((element) => {
          element.textContent = moneyPrice;
        });
        price.querySelectorAll('.price__sale .price-item--sale, .price__sale .price-item--last').forEach((element) => {
          element.textContent = moneyPrice;
        });
        price.querySelectorAll('.price__sale .price-item--regular').forEach((element) => {
          element.textContent = isOnSale ? this.formatMoney(compareAtPrice) : '';
        });
      }

      updateSkuFromVariant(variant) {
        const sku = this.querySelector(`#Sku-${this.dataset.section}`);
        if (!sku) return;

        sku.textContent = variant.sku || '';
        sku.classList.toggle('hidden', !variant.sku);
      }

      updateTrustpilotWidgetsFromVariant(variant) {
        const sku = variant?.sku;
        if (!sku) return;

        document.querySelectorAll('.trustpilot-widget[data-sku]:not(.rating)').forEach((widget) => {
          const accordion = widget.closest('details');
          const accordionTitle =
            accordion?.querySelector('summary .accordion__title, summary')?.textContent?.trim() || '';

          if (accordion && !/reviews/i.test(accordionTitle)) return;
          if (widget.dataset.sku === sku) return;

          widget.dataset.sku = sku;
          widget.innerHTML = '';

          if (
            window.Trustpilot &&
            typeof window.Trustpilot.loadFromElement === 'function' &&
            (widget.offsetParent !== null || widget.getClientRects().length > 0)
          ) {
            window.Trustpilot.loadFromElement(widget);
          }
        });
      }

      updateInventoryFromVariant(variant) {
        const inventory = this.querySelector(`#Inventory-${this.dataset.section}`);
        if (!inventory) return;

        if (!variant.available) {
          inventory.classList.add('hidden');
          inventory.classList.remove('visibility-hidden');
          inventory.textContent = '';
          return;
        }

        inventory.classList.remove('hidden', 'visibility-hidden');
      }

      updateAddToCartState(variant) {
        const submitButton = this.querySelector('#custom-add-to-cart');
        const variantInputs = this.querySelectorAll('input.product-variant-id[name="id"]');
        if (!submitButton) return;

        const disabled = !variant.available;
        variantInputs.forEach((input) => {
          input.disabled = disabled;
          input.value = variant.id;
        });

        submitButton.disabled = disabled;
        submitButton.classList.toggle('disabled', disabled);
        submitButton.setAttribute('aria-disabled', disabled ? 'true' : 'false');

        const label = submitButton.querySelector('span');
        if (label) label.textContent = disabled ? window.variantStrings.soldOut : window.variantStrings.addToCart;
      }

      updateMediaFromVariant(variant, productData) {
        if (productData && productData.id && String(productData.id) !== this.dataset.productId) {
          this.dataset.productId = productData.id;
          this.replaceGalleryImages(productData, variant);
          return;
        }

        if (variant.featured_media?.id) {
          this.querySelector('media-gallery')?.setActiveMedia?.(`${this.dataset.section}-${variant.featured_media.id}`, true);
        }
      }

      replaceGalleryImages(productData, variant) {
        const imageUrls = this.getProductImageUrls(productData, variant);
        if (!imageUrls.length) return;

        const gallery = this.querySelector('media-gallery');
        const list = gallery?.querySelector('ul');
        const template = list?.querySelector('li.product__media-item');
        if (!gallery || !list || !template) return;

        const items = Array.from(list.querySelectorAll('li.product__media-item'));
        imageUrls.forEach((src, index) => {
          const item = items[index] || template.cloneNode(true);
          const mediaId = `${this.dataset.section}-strap-${productData.handle || productData.id}-${index}`;

          item.id = `Slide-${mediaId}`;
          item.dataset.mediaId = mediaId;
          item.classList.toggle('is-active', index === 0);
          item.classList.remove('scroll-trigger--offscreen');

          item.querySelectorAll('img').forEach((image) => this.updateImageElement(image, src, productData.title, index));
          item.querySelectorAll('[data-media-id]').forEach((control) => {
            control.dataset.mediaId = mediaId;
          });

          if (!items[index]) list.appendChild(item);
        });

        items.slice(imageUrls.length).forEach((item) => item.remove());
        this.replaceThumbnailImages(productData, imageUrls);
        gallery.setActiveMedia?.(list.querySelector('li.product__media-item')?.dataset.mediaId, true);
        window.WatchStrapRefreshScrollAnimations?.(gallery);
      }

      replaceThumbnailImages(productData, imageUrls) {
        const thumbnailList = this.querySelector(`#Slider-Thumbnails-${this.dataset.section}`);
        const template = thumbnailList?.querySelector('li');
        if (!thumbnailList || !template) return;

        const items = Array.from(thumbnailList.querySelectorAll('li'));
        imageUrls.forEach((src, index) => {
          const item = items[index] || template.cloneNode(true);
          const mediaId = `${this.dataset.section}-strap-${productData.handle || productData.id}-${index}`;
          const button = item.querySelector('button');

          item.id = `Slide-Thumbnails-${mediaId}`;
          item.dataset.target = mediaId;
          if (button) button.dataset.target = mediaId;
          item.querySelectorAll('img').forEach((image) => this.updateImageElement(image, src, productData.title, index));

          if (!items[index]) thumbnailList.appendChild(item);
        });

        items.slice(imageUrls.length).forEach((item) => item.remove());
      }

      updateImageElement(image, src, alt, index) {
        if (!image || !src) return;

        image.src = this.imageWithWidth(src, index === 0 ? 1206 : 823);
        image.srcset = [246, 493, 600, 713, 823, 990, 1100, 1206, 1346, 1426, 1646, 1946]
          .map((width) => `${this.imageWithWidth(src, width)} ${width}w`)
          .join(', ');
        image.alt = alt || image.alt || '';
        image.loading = index === 0 ? 'eager' : 'lazy';
        image.fetchPriority = index === 0 ? 'high' : 'auto';
        image.removeAttribute('hidden');
      }

      getProductImageUrls(productData, variant = null) {
        const urls = [];
        const featuredImage = variant?.featured_image?.src || variant?.featured_media?.preview_image?.src;
        if (featuredImage) urls.push(featuredImage);

        (productData?.images || []).forEach((image) => {
          const src = typeof image === 'string' ? image : image?.src;
          if (src && !urls.includes(src)) urls.push(src);
        });

        return urls;
      }

      imageWithWidth(src, width) {
        if (!src) return '';

        const normalizedSrc = src.startsWith('//') ? `https:${src}` : src;
        try {
          const url = new URL(normalizedSrc, window.location.origin);
          url.searchParams.set('width', width);
          return url.toString();
        } catch (error) {
          return normalizedSrc;
        }
      }

      getProductData() {
        try {
          return this.normalizeProductData(JSON.parse(this.querySelector('#product-json')?.textContent || '{}'));
        } catch (error) {
          return {};
        }
      }

      getProductOptionNames(productData = this.getProductData(), variantSelects = this.variantSelectors) {
        const optionNames = (productData?.options || []).map((option) =>
          typeof option === 'string' ? option : option?.name
        ).filter(Boolean);

        return optionNames.length ? optionNames : variantSelects?.optionNames || [];
      }

      formatMoney(cents) {
        if (window.Shopify?.formatMoney) {
          const moneyFormat = this.getMoneyFormat();
          return window.Shopify.formatMoney(cents, moneyFormat);
        }

        const currencyData = this.getCurrencyData();
        const amount = Number(cents || 0) / 100;
        const formatted = amount.toLocaleString(undefined, {
          minimumFractionDigits: 2,
          maximumFractionDigits: 2,
        });
        return `${currencyData.symbol || ''}${formatted}${currencyData.iso_code ? ` ${currencyData.iso_code}` : ''}`.trim();
      }

      getMoneyFormat() {
        try {
          return JSON.parse(this.querySelector('#money-format')?.textContent || '"${{amount}}"');
        } catch (error) {
          return '${{amount}}';
        }
      }

      getCurrencyData() {
        try {
          return JSON.parse(this.querySelector('#currency-data')?.textContent || '{}');
        } catch (error) {
          return {};
        }
      }

      resetProductFormState() {
        const productForm = this.productForm;
        productForm?.toggleSubmitButton(true);
        productForm?.handleErrorMessage();
      }

      handleSwapProduct(productUrl, updateFullPage) {
        return (html) => {
          this.productModal?.remove();

          const selector = updateFullPage ? "product-info[id^='MainProduct']" : 'product-info';
          const variant = this.getSelectedVariant(html.querySelector(selector));
          this.updateURL(productUrl, variant?.id);

          if (updateFullPage) {
            document.querySelector('head title').innerHTML = html.querySelector('head title').innerHTML;

            HTMLUpdateUtility.viewTransition(
              document.querySelector('main'),
              html.querySelector('main'),
              this.preProcessHtmlCallbacks,
              this.postProcessHtmlCallbacks
            );
          } else {
            HTMLUpdateUtility.viewTransition(
              this,
              html.querySelector('product-info'),
              this.preProcessHtmlCallbacks,
              this.postProcessHtmlCallbacks
            );
          }
        };
      }

      renderProductInfo({ requestUrl, targetId, callback }) {
        this.abortController?.abort();
        this.abortController = new AbortController();

        fetch(requestUrl, { signal: this.abortController.signal })
          .then((response) => response.text())
          .then((responseText) => {
            this.pendingRequestUrl = null;
            const html = new DOMParser().parseFromString(responseText, 'text/html');
            callback(html);
          })
          .then(() => {
            // set focus to last clicked option value
           // document.querySelector(`#${targetId}`)?.focus();
          })
          .catch((error) => {
            if (error.name === 'AbortError') {
              console.log('Fetch aborted by user');
            } else {
              console.error(error);
            }
          });
      }

      getSelectedVariant(productInfoNode) {
        const selectedVariant = productInfoNode.querySelector('variant-selects [data-selected-variant]')?.innerHTML;
        return !!selectedVariant ? JSON.parse(selectedVariant) : null;
      }

      buildRequestUrlWithParams(url, optionValues, shouldFetchFullPage = false) {
        const params = [];

        !shouldFetchFullPage && params.push(`section_id=${this.sectionId}`);

        if (optionValues.length) {
          params.push(`option_values=${optionValues.join(',')}`);
        }

        return `${url}?${params.join('&')}`;
      }

      updateOptionValues(html) {
        const variantSelects = html.querySelector('variant-selects');
        if (variantSelects) {
          HTMLUpdateUtility.viewTransition(this.variantSelectors, variantSelects, this.preProcessHtmlCallbacks);
        }
      }

      handleUpdateProductInfo(productUrl) {
        return (html) => {
          const variant = this.getSelectedVariant(html);

          this.pickupAvailability?.update(variant);
          this.updateOptionValues(html);
          this.updateURL(productUrl, variant?.id);
          this.updateVariantInputs(variant?.id);

          if (!variant) {
            this.setUnavailable();
            return;
          }

          this.updateMedia(html, variant?.featured_media?.id);

          const updateSourceFromDestination = (id, shouldHide = (source) => false) => {
            const source = html.getElementById(`${id}-${this.sectionId}`);
            const destination = this.querySelector(`#${id}-${this.dataset.section}`);
            if (source && destination) {
              destination.innerHTML = source.innerHTML;
              destination.classList.toggle('hidden', shouldHide(source));
            }
          };

          updateSourceFromDestination('price');
          updateSourceFromDestination('Sku', ({ classList }) => classList.contains('hidden'));
          updateSourceFromDestination('Inventory', ({ innerText }) => innerText === '');
          updateSourceFromDestination('Volume');
          updateSourceFromDestination('Price-Per-Item', ({ classList }) => classList.contains('hidden'));

          this.updateQuantityRules(this.sectionId, html);
          this.querySelector(`#Quantity-Rules-${this.dataset.section}`)?.classList.remove('hidden');
          this.querySelector(`#Volume-Note-${this.dataset.section}`)?.classList.remove('hidden');

          this.productForm?.toggleSubmitButton(
            html.getElementById(`ProductSubmitButton-${this.sectionId}`)?.hasAttribute('disabled') ?? true,
            window.variantStrings.soldOut
          );

          publish(PUB_SUB_EVENTS.variantChange, {
            data: {
              sectionId: this.sectionId,
              html,
              variant,
            },
          });
        };
      }

      updateVariantInputs(variantId) {
        this.querySelectorAll(
          `#product-form-${this.dataset.section}, #product-form-installment-${this.dataset.section}`
        ).forEach((productForm) => {
          const input = productForm.querySelector('input[name="id"]');
          input.value = variantId ?? '';
          input.dispatchEvent(new Event('change', { bubbles: true }));
        });
      }

      updateURL(url, variantId) {
        this.querySelector('share-button')?.updateUrl(
          `${window.shopUrl}${url}${variantId ? `?variant=${variantId}` : ''}`
        );

        if (this.dataset.updateUrl === 'false') return;
        window.history.replaceState({}, '', `${url}${variantId ? `?variant=${variantId}` : ''}`);
      }

      setUnavailable() {
        const submitButton = this.querySelector('#custom-add-to-cart');
        const label = submitButton?.querySelector('span');
        this.lastAppliedVariantKey = null;
        this.querySelectorAll('input.product-variant-id[name="id"]').forEach((input) => {
          input.value = '';
          input.disabled = true;
        });

        if (submitButton) {
          submitButton.disabled = true;
          submitButton.classList.add('disabled');
          submitButton.setAttribute('aria-disabled', 'true');
        }
        if (label) label.textContent = window.variantStrings.unavailable;

        const selectors = ['price', 'Inventory', 'Sku', 'Price-Per-Item', 'Volume-Note', 'Volume', 'Quantity-Rules']
          .map((id) => `#${id}-${this.dataset.section}`)
          .join(', ');
        this.querySelectorAll(selectors).forEach(({ classList }) => classList.add('hidden'));
      }

      updateMedia(html, variantFeaturedMediaId) {
        if (!variantFeaturedMediaId) return;

        const mediaGallerySource = this.querySelector('media-gallery ul');
        const mediaGalleryDestination = html.querySelector(`media-gallery ul`);

        const refreshSourceData = () => {
          if (this.hasAttribute('data-zoom-on-hover')) enableZoomOnHover(2);
          const mediaGallerySourceItems = Array.from(mediaGallerySource.querySelectorAll('li[data-media-id]'));
          const sourceSet = new Set(mediaGallerySourceItems.map((item) => item.dataset.mediaId));
          const sourceMap = new Map(
            mediaGallerySourceItems.map((item, index) => [item.dataset.mediaId, { item, index }])
          );
          return [mediaGallerySourceItems, sourceSet, sourceMap];
        };

        if (mediaGallerySource && mediaGalleryDestination) {
          let [mediaGallerySourceItems, sourceSet, sourceMap] = refreshSourceData();
          const mediaGalleryDestinationItems = Array.from(
            mediaGalleryDestination.querySelectorAll('li[data-media-id]')
          );
          const destinationSet = new Set(mediaGalleryDestinationItems.map(({ dataset }) => dataset.mediaId));
          let shouldRefresh = false;

          // add items from new data not present in DOM
          for (let i = mediaGalleryDestinationItems.length - 1; i >= 0; i--) {
            if (!sourceSet.has(mediaGalleryDestinationItems[i].dataset.mediaId)) {
              mediaGallerySource.prepend(mediaGalleryDestinationItems[i]);
              shouldRefresh = true;
            }
          }

          // remove items from DOM not present in new data
          for (let i = 0; i < mediaGallerySourceItems.length; i++) {
            if (!destinationSet.has(mediaGallerySourceItems[i].dataset.mediaId)) {
              mediaGallerySourceItems[i].remove();
              shouldRefresh = true;
            }
          }

          // refresh
          if (shouldRefresh) [mediaGallerySourceItems, sourceSet, sourceMap] = refreshSourceData();

          // if media galleries don't match, sort to match new data order
          mediaGalleryDestinationItems.forEach((destinationItem, destinationIndex) => {
            const sourceData = sourceMap.get(destinationItem.dataset.mediaId);

            if (sourceData && sourceData.index !== destinationIndex) {
              mediaGallerySource.insertBefore(
                sourceData.item,
                mediaGallerySource.querySelector(`li:nth-of-type(${destinationIndex + 1})`)
              );

              // refresh source now that it has been modified
              [mediaGallerySourceItems, sourceSet, sourceMap] = refreshSourceData();
            }
          });
        }

        // set featured media as active in the media gallery
        this.querySelector(`media-gallery`)?.setActiveMedia?.(
          `${this.dataset.section}-${variantFeaturedMediaId}`,
          true
        );

        // update media modal
        const modalContent = this.productModal?.querySelector(`.product-media-modal__content`);
        const newModalContent = html.querySelector(`product-modal .product-media-modal__content`);
        if (modalContent && newModalContent) modalContent.innerHTML = newModalContent.innerHTML;
      }

      setQuantityBoundries() {
        const data = {
          cartQuantity: this.quantityInput.dataset.cartQuantity ? parseInt(this.quantityInput.dataset.cartQuantity) : 0,
          min: this.quantityInput.dataset.min ? parseInt(this.quantityInput.dataset.min) : 1,
          max: this.quantityInput.dataset.max ? parseInt(this.quantityInput.dataset.max) : null,
          step: this.quantityInput.step ? parseInt(this.quantityInput.step) : 1,
        };

        let min = data.min;
        const max = data.max === null ? data.max : data.max - data.cartQuantity;
        if (max !== null) min = Math.min(min, max);
        if (data.cartQuantity >= data.min) min = Math.min(min, data.step);

        this.quantityInput.min = min;

        if (max) {
          this.quantityInput.max = max;
        } else {
          this.quantityInput.removeAttribute('max');
        }
        this.quantityInput.value = min;

        publish(PUB_SUB_EVENTS.quantityUpdate, undefined);
      }

      fetchQuantityRules() {
        const currentVariantId = this.productForm?.variantIdInput?.value;
        if (!currentVariantId) return;

        this.querySelector('.quantity__rules-cart .loading__spinner').classList.remove('hidden');
        return fetch(`${this.dataset.url}?variant=${currentVariantId}&section_id=${this.dataset.section}`)
          .then((response) => response.text())
          .then((responseText) => {
            const html = new DOMParser().parseFromString(responseText, 'text/html');
            this.updateQuantityRules(this.dataset.section, html);
          })
          .catch((e) => console.error(e))
          .finally(() => this.querySelector('.quantity__rules-cart .loading__spinner').classList.add('hidden'));
      }

      updateQuantityRules(sectionId, html) {
        if (!this.quantityInput) return;
        this.setQuantityBoundries();

        const quantityFormUpdated = html.getElementById(`Quantity-Form-${sectionId}`);
        const selectors = ['.quantity__input', '.quantity__rules', '.quantity__label'];
        for (let selector of selectors) {
          const current = this.quantityForm.querySelector(selector);
          const updated = quantityFormUpdated.querySelector(selector);
          if (!current || !updated) continue;
          if (selector === '.quantity__input') {
            const attributes = ['data-cart-quantity', 'data-min', 'data-max', 'step'];
            for (let attribute of attributes) {
              const valueUpdated = updated.getAttribute(attribute);
              if (valueUpdated !== null) {
                current.setAttribute(attribute, valueUpdated);
              } else {
                current.removeAttribute(attribute);
              }
            }
          } else {
            current.innerHTML = updated.innerHTML;
          }
        }
      }

      get productForm() {
        return this.querySelector(`product-form`);
      }

      get productModal() {
        return document.querySelector(`#ProductModal-${this.dataset.section}`);
      }

      get pickupAvailability() {
        return this.querySelector(`pickup-availability`);
      }

      get variantSelectors() {
        return this.querySelector('variant-selects');
      }

      get relatedProducts() {
        const relatedProductsSectionId = SectionId.getIdForSection(
          SectionId.parseId(this.sectionId),
          'related-products'
        );
        return document.querySelector(`product-recommendations[data-section-id^="${relatedProductsSectionId}"]`);
      }

      get quickOrderList() {
        const quickOrderListSectionId = SectionId.getIdForSection(
          SectionId.parseId(this.sectionId),
          'quick_order_list'
        );
        return document.querySelector(`quick-order-list[data-id^="${quickOrderListSectionId}"]`);
      }

      get sectionId() {
        return this.dataset.originalSection || this.dataset.section;
      }
    }
  );
}
